import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import HttpService from "../services/httpService";
import { setLoading, setSnackInfo } from "../redux/actions/appAction";
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  button: {
    marginTop: theme.spacing(2),
  },
  formRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  formItem: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  formItemLast: {
    flex: 1,
  },
  table: {
    minWidth: 650,
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  createButton: {
    marginBottom: theme.spacing(2),
    float: "right",
  },
  formField: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  tableTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
    fontSize: '1.2rem',
    textAlign: 'center'
  },
  approveButton: {
    backgroundColor: '#4caf50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#388e3c',
    },
  },
  tableHeader: {
    backgroundColor: '#1C375D',
    "& .MuiTableCell-head": {
      color: "#fff",
      fontWeight: 600,
    },
  }
}));
const OverheadProjectApproval = (props) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({
    projectType: "Overhead",
    projectName: "",
    city: { _id: "", name: "" },
    experienceCenter: { _id: "", name: "" },
    area: "",
    startDate: null,
    completionDate: null,
  });
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const fetchProjects = async () => {
    try {
      props.setLoading(true);
      const response = await HttpService.getOverheadProjectsForApproval({
        page: page + 1,
        limit: rowsPerPage
      });
      setProjects(response.data.projectsData);
      setTotalCount(response.data.totalCount);
      props.setLoading(false);
    } catch (error) {
      props.setLoading(false);
      props.setSnackInfo(error.message || "Failed to fetch projects", "error");
    }
  };
  const handleViewProject = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setSelectedProject(project);
    setOpenViewDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenViewDialog(false);
    setSelectedProject(null);
  };
  const handleProjectAction = async (action) => {
    try {
      props.setLoading(true);
      const response = await HttpService.updateCapexProjectStatus(selectedProject.id, action);
      if (response.status === 200) {
        props.setSnackInfo(`Project ${action} Successfully`, "success");
        handleCloseDialog();
        fetchProjects();
      }
    } catch (error) {
      props.setSnackInfo(error.message || "Failed to approve project", "error");
    } finally {
      props.setLoading(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchProjects();
  }, [page, rowsPerPage]);

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.tableTitle}>
        Overhead Project Approvals
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} aria-label="approvals table">
          <TableHead>
            <TableRow className={classes.tableHeader}>
            <TableCell style={{textAlign: 'center'}}>Project Number</TableCell>
              <TableCell style={{textAlign: 'center'}}>Requested Date</TableCell>
              <TableCell style={{textAlign: 'center'}}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell component='th' style={{textAlign: 'center'}}>{project.projectNo}</TableCell>
                <TableCell component='th' style={{textAlign: 'center'}}>
                  {project.createdAt
                    ? moment(project.createdAt).format("YYYY-MM-DD")
                    : "NA"}
                </TableCell>
                <TableCell component='th' style={{textAlign: 'center'}}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleViewProject(project.id)}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      <Dialog
      open={openViewDialog}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Project Details</DialogTitle>
      <DialogContent>
        {selectedProject && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Project Type</Typography>
              <Typography>{selectedProject.project_type_name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Project Number</Typography>
              <Typography>{selectedProject.projectNo}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Experience Center</Typography>
              <Typography>{selectedProject.expCtr}</Typography>
            </Grid>
            {/* <Grid item xs={6}>
              <Typography variant="subtitle2">City</Typography>
              <Typography>{selectedProject.city}</Typography>
            </Grid> */}
            {/* <Grid item xs={6}>
              <Typography variant="subtitle2">Area</Typography>
              <Typography>{selectedProject.area}</Typography>
            </Grid> */}
            <Grid item xs={6}>
              <Typography variant="subtitle2">Start Date</Typography>
              <Typography>
                {selectedProject.startDate
                  ? moment(selectedProject.startDate).format("YYYY-MM-DD")
                  : "NA"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Project Completion</Typography>
              <Typography>
                {selectedProject.projectCompletion
                  ? moment(selectedProject.projectCompletion).format("YYYY-MM-DD")
                  : "NA"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Status</Typography>
              <Typography>{selectedProject.projectCreationStatus}</Typography>
            </Grid>
          </Grid>
        )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="default">
            Cancel
          </Button>
          <Button 
            onClick={() => handleProjectAction("Rejected")}
            color="secondary" 
            variant="contained"
          >
            Reject
          </Button>
          <Button 
            onClick={() => handleProjectAction("Approved")} 
            color="primary" 
            variant="contained"
            className={classes.approveButton}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default connect(null, { setLoading, setSnackInfo })(OverheadProjectApproval);